import { type CSSProperties, type ReactNode, useEffect, useLayoutEffect, useState } from 'react';

import { clearRuntimeConfig, setRuntimeConfig } from '@repo/env/frontend';

import { PortalContainerProvider } from '@/api/evo';
import { clearConfig, setConfig } from '@/lib/config/config-registry';
import type { TemplateBuilderConfig } from '@/lib/config/template-builder-config';
import { TemplateBuilderFlagsProvider } from '@/template-builder-flags.provider';

/**
 * Host entry point for embedding `@rotaready/template-builder-evo` as a library.
 *
 * Writes the host `config` into the module-level registry so the non-React API
 * clients can read auth + base URLs, captures the initial `config.environment`
 * as `@repo/env`'s immutable runtime override so `@repo/flags`-gated behaviour
 * (and any future `@repo/env`-derived behaviour) resolves against the host's
 * real deployment stage rather than whatever this package was built with, scopes
 * the app under a `.rrtb-root` element, and applies any host theme overrides
 * (Q5) as CSS custom properties on that root (custom properties inherit, so
 * they override the app's `:root`/`:host` tokens for this subtree). Render
 * the app inside it:
 *
 * ```tsx
 * <TemplateBuilderProvider config={config}>
 *   <TemplateBuilderApp />
 * </TemplateBuilderProvider>
 * ```
 *
 * The registry and runtime override are both set **during render** (not in
 * an effect) so they're available before any child effect/fetch runs — child
 * effects fire before a parent's. The effect only clears them on unmount.
 *
 * A second, library-owned `.rrtb-root` is created as a direct child of
 * `document.body` to host Radix/Sonner portals. This keeps portalled
 * overlays viewport-relative while ensuring they inherit the scoped CSS reset
 * and the same `config.theme.cssVariables` as the regular embedded root.
 */
export function TemplateBuilderProvider({
  config,
  children,
}: {
  config: TemplateBuilderConfig;
  children: ReactNode;
}): ReactNode {
  const [environment] = useState(config.environment);
  const resolvedConfig = config.environment === environment ? config : { ...config, environment };
  setConfig(resolvedConfig);
  setRuntimeConfig({ environment });

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  const themeVariables = resolvedConfig.theme?.cssVariables;
  const themeVariant = resolvedConfig.theme?.variant === 'rotaready' ? 'rotaready' : 'native';

  useEffect(() => {
    return () => {
      clearConfig();
      clearRuntimeConfig();
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const host = document.createElement('div');
    host.className = 'rrtb-root';
    host.setAttribute('data-rrtb-portal-root', '');
    // The shared `.rrtb-root` reset sets `height: 100%`; the empty portal host
    // should not participate in body sizing.
    host.style.height = 'auto';
    document.body.appendChild(host);
    setPortalContainer(host);

    return () => {
      host.remove();
    };
  }, []);

  useLayoutEffect(() => {
    if (!portalContainer) return;
    portalContainer.setAttribute('data-rrtb-theme', themeVariant);
    if (!themeVariables) return;

    for (const [key, value] of Object.entries(themeVariables)) {
      portalContainer.style.setProperty(key, value);
    }

    return () => {
      for (const key of Object.keys(themeVariables)) {
        portalContainer.style.removeProperty(key);
      }
    };
  }, [portalContainer, themeVariables, themeVariant]);

  return (
    <TemplateBuilderFlagsProvider config={resolvedConfig}>
      <PortalContainerProvider portalContainer={portalContainer}>
        <div
          className="rrtb-root"
          data-rrtb-theme={themeVariant}
          style={themeVariables as CSSProperties | undefined}
        >
          {children}
        </div>
      </PortalContainerProvider>
    </TemplateBuilderFlagsProvider>
  );
}
